<template>
  <b-modal
    id="chantier-create-modal"
    title="Nouveau chantier"
    ok-title="Créer"
    cancel-title="Annuler"
    :ok-disabled="creating"
    centered
    size="lg"
    @ok.prevent="submitCreate"
  >
    <b-alert
      :show="warnExistingChantier"
      variant="warning"
      class="mb-1"
    >
      <div class="alert-body">
        Au moins un chantier a déjà été créé sur cette fiche incident.
      </div>
    </b-alert>
    <b-form-group
      label="Nom"
      label-for="create-nom"
    >
      <b-form-input
        id="create-nom"
        v-model="createModel.nom"
      />
    </b-form-group>
    <b-form-group
      label="Description"
      label-for="create-desc"
    >
      <b-form-textarea
        id="create-desc"
        v-model="createModel.description"
        rows="3"
      />
    </b-form-group>
    <b-form-group label="Responsable">
      <searchable-vue-select
        v-model="selectedResponsable"
        :options="utilisateursOptions"
        label="nomComplet"
        :has-next-page="utilisateursOptions.length < utilisateursTotalCount"
        placeholder="Choisir un responsable"
        @option:clear="searchUtilisateurs()"
        @load-next-page="(params) => searchUtilisateurs(params)"
      />
    </b-form-group>
    <div class="row">
      <div class="col-12 col-md-6">
        <b-form-group
          label="Catégorie"
          label-for="create-cat"
        >
          <b-form-select
            id="create-cat"
            v-model="createModel.categorieId"
            :options="createCategorieOptions"
          />
        </b-form-group>
      </div>
      <div class="col-12 col-md-6">
        <b-form-group
          label="Origine"
          label-for="create-origine"
        >
          <b-form-select
            id="create-origine"
            v-model="createModel.origineId"
            :options="createOrigineOptions"
          />
        </b-form-group>
      </div>
      <div class="col-12 col-md-6">
        <b-form-group
          label="Sous-catégorie"
          label-for="create-subcat"
        >
          <b-form-input
            id="create-subcat"
            v-model="createModel.sousCategorie"
          />
        </b-form-group>
      </div>
      <div class="col-12 col-md-6">
        <b-form-group
          label="Visibilité"
          label-for="create-vis"
        >
          <b-form-select
            id="create-vis"
            v-model="createModel.visibilite"
            :options="createVisibiliteOptions"
          />
        </b-form-group>
      </div>
      <div class="col-12 col-md-6">
        <b-form-group
          label="Date de début"
          label-for="create-datedebut"
        >
          <b-form-input
            id="create-datedebut"
            v-model="createModel.dateDebut"
            type="date"
          />
        </b-form-group>
      </div>
      <div class="col-12 col-md-6">
        <b-form-group
          label="Échéance"
          label-for="create-echeance"
        >
          <b-form-input
            id="create-echeance"
            v-model="createModel.dateEcheance"
            type="date"
          />
        </b-form-group>
      </div>
      <div class="col-12 col-md-6">
        <b-form-group
          label="Priorité"
          label-for="create-priorite"
        >
          <b-form-select
            id="create-priorite"
            v-model="createModel.priorite"
            :options="createPrioriteOptions"
          />
        </b-form-group>
      </div>
    </div>
    <b-form-group
      label="Livrables"
      label-for="create-livrables"
    >
      <b-form-textarea
        id="create-livrables"
        v-model="createModel.livrables"
        rows="3"
      />
    </b-form-group>
    <b-form-group label="Chantier parent (optionnel)">
      <searchable-vue-select
        v-model="selectedParent"
        :options="listChantiers"
        label="nom"
        :has-next-page="listChantiers.length < chantiersTotalCount"
        placeholder="Rattacher à un chantier parent"
        @option:clear="searchChantiers()"
        @load-next-page="(params) => searchChantiers(params)"
      />
    </b-form-group>
    <b-form-group
      label="Projet d'amélioration continue (optionnel)"
      label-for="create-projet"
    >
      <b-form-select
        id="create-projet"
        v-model="createModel.projetId"
        :options="createProjetOptions"
      />
    </b-form-group>
    <p
      v-if="createError"
      class="text-danger mb-0"
    >
      {{ createError }}
    </p>
  </b-modal>
</template>

<script>
import {
  BModal,
  BFormGroup,
  BFormInput,
  BFormSelect,
  BFormTextarea,
  BAlert,
} from 'bootstrap-vue'
import SearchableVueSelect from '@/components/selects/SearchableVueSelect.vue'

// Modale de création de chantier, partagée entre la liste (« Nouveau chantier ») et le détail
// (« Créer un chantier de suivi », pré-rempli). N'effectue PAS la navigation : émet @created(newId).
export default {
  name: 'ChantierCreateModal',
  components: {
    BModal,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BFormTextarea,
    BAlert,
    SearchableVueSelect,
  },
  data() {
    return {
      createModel: this.emptyCreateModel(),
      selectedResponsable: null,
      utilisateursOptions: [],
      utilisateursTotalCount: 0,
      selectedParent: null,
      listChantiers: [],
      chantiersTotalCount: 0,
      creating: false,
      createError: null,
      warnExistingChantier: false,
      categories: [],
      origines: [],
      priorites: [],
      projets: [],
      referentielsLoaded: false,
    }
  },
  computed: {
    createCategorieOptions() {
      return [
        { value: null, text: 'Sélectionner une catégorie', disabled: true },
        ...this.categories.map(c => ({ value: c.id, text: c.libelle })),
      ]
    },
    createOrigineOptions() {
      return [
        { value: null, text: 'Sélectionner une origine', disabled: true },
        ...this.origines.map(o => ({ value: o.id, text: o.libelle })),
      ]
    },
    createVisibiliteOptions() {
      return [
        { value: 0, text: 'Privé' },
        { value: 1, text: 'Public' },
      ]
    },
    createPrioriteOptions() {
      return this.priorites.map(p => ({ value: p.id, text: p.valeur }))
    },
    createProjetOptions() {
      return [
        { value: null, text: 'Aucun projet' },
        ...this.projets.map(p => ({ value: p.id, text: p.nom })),
      ]
    },
  },
  methods: {
    emptyCreateModel() {
      return {
        nom: '',
        description: '',
        categorieId: null,
        origineId: null,
        sousCategorie: '',
        visibilite: 0,
        dateEcheance: '',
        dateDebut: new Date().toISOString().slice(0, 10),
        livrables: '',
        priorite: null,
        ficheIncidentId: null,
        projetId: null,
      }
    },
    defautPrioriteId() {
      const def = this.priorites.find(p => p.defaut) || this.priorites[0]
      return def ? def.id : null
    },
    async loadReferentiels() {
      if (this.referentielsLoaded) return
      try {
        const [categories, origines, priorites, projets] = await Promise.all([
          this.$http.myressif.chantiers.listCategories(),
          this.$http.myressif.chantiers.listOrigines(),
          this.$http.myressif.chantiers.getPriorites(),
          this.$http.myressif.chantierProjets.listRef(),
        ])
        this.categories = (categories && categories.items) || []
        this.origines = (origines && origines.items) || []
        this.priorites = priorites || []
        this.projets = projets || []
        this.referentielsLoaded = true
      } catch (e) {
        this.categories = []
        this.origines = []
        this.priorites = []
        this.projets = []
      }
    },
    // Ouvre la modale (méthode publique appelée via ref). prefill : { categorieId, origineId, responsable }.
    async open(prefill) {
      await this.loadReferentiels()
      this.createModel = this.emptyCreateModel()
      this.createModel.priorite = this.defautPrioriteId()
      this.selectedResponsable = null
      this.selectedParent = null
      this.createError = null
      this.warnExistingChantier = false
      if (prefill) {
        if (prefill.categorieId) this.createModel.categorieId = prefill.categorieId
        if (prefill.origineId) this.createModel.origineId = prefill.origineId
        if (prefill.responsable) {
          const r = prefill.responsable
          this.selectedResponsable = {
            id: r.id,
            nomComplet: r.nomComplet || `${r.prenom || ''} ${r.nom || ''}`.trim() || r.id,
          }
        }
        if (prefill.parent) {
          this.selectedParent = { id: prefill.parent.id, nom: prefill.parent.nom }
        }
        if (prefill.description) this.createModel.description = prefill.description
        if (prefill.dateDebut) this.createModel.dateDebut = prefill.dateDebut
        if (prefill.dateEcheance) this.createModel.dateEcheance = prefill.dateEcheance
        if (prefill.ficheIncidentId) this.createModel.ficheIncidentId = prefill.ficheIncidentId
        if (prefill.projetId) this.createModel.projetId = prefill.projetId
        this.warnExistingChantier = !!prefill.warnExistingChantier
        // Pré-remplit la priorité depuis la gravité de la fiche par correspondance de libellé
        // (Faible/Moyenne/Haute) — indépendant de l'ordre du référentiel. Sinon, garde le défaut.
        if (typeof prefill.gravite === 'number') {
          const graviteLabels = ['faible', 'moyenne', 'haute']
          const cible = graviteLabels[prefill.gravite]
          const norm = v => (v || '').trim().toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '')
          const match = this.priorites.find(p => norm(p.valeur) === cible)
          if (match) this.createModel.priorite = match.id
        }
      }
      await this.searchUtilisateurs()
      await this.searchChantiers()
      this.$bvModal.show('chantier-create-modal')
    },
    async searchUtilisateurs(params) {
      const search = (params && params.search) || ''
      const reset = !params || params.reset
      const pageNumber = reset ? 1 : ((params && params.pageNumber) || 1)
      try {
        const response = await this.$http.myressif.utilisateurs
          .paginatedList(pageNumber, 10, search, false, 'nom', false)
        const items = (response && response.items) || []
        this.utilisateursOptions = reset
          ? items
          : this.utilisateursOptions.concat(items.filter(ri => !this.utilisateursOptions.some(e => e.id === ri.id)))
        this.utilisateursTotalCount = (response && response.totalCount) || this.utilisateursOptions.length
      } catch (e) {
        if (reset) { this.utilisateursOptions = []; this.utilisateursTotalCount = 0 }
      }
      return this.utilisateursOptions
    },
    async searchChantiers(params) {
      const search = (params && params.search) || ''
      const reset = !params || params.reset
      const pageNumber = reset ? 1 : ((params && params.pageNumber) || 1)
      try {
        const response = await this.$http.myressif.chantiers.search({ pageNumber, pageSize: 10, search: search || undefined })
        const items = (response && response.items) || []
        this.listChantiers = reset
          ? items
          : this.listChantiers.concat(items.filter(ri => !this.listChantiers.some(e => e.id === ri.id)))
        this.chantiersTotalCount = (response && response.totalCount) || this.listChantiers.length
      } catch (e) {
        if (reset) { this.listChantiers = []; this.chantiersTotalCount = 0 }
      }
      return this.listChantiers
    },
    async submitCreate() {
      if (!this.createModel.nom || !this.createModel.categorieId || !this.createModel.origineId) {
        this.createError = 'Le nom, la catégorie et l’origine sont obligatoires.'
        return
      }
      if (!this.selectedResponsable) {
        this.createError = 'Le responsable est obligatoire.'
        return
      }
      if (this.creating) return
      this.creating = true
      this.createError = null
      try {
        const model = {
          nom: this.createModel.nom,
          description: this.createModel.description || undefined,
          categorieId: this.createModel.categorieId,
          origineId: this.createModel.origineId,
          sousCategorie: this.createModel.sousCategorie || undefined,
          visibilite: this.createModel.visibilite,
          dateEcheance: this.createModel.dateEcheance || undefined,
          responsableId: this.selectedResponsable.id,
          dateDebut: this.createModel.dateDebut || undefined,
          livrables: this.createModel.livrables || undefined,
          priorite: this.createModel.priorite !== null ? this.createModel.priorite : undefined,
          chantierParentId: this.selectedParent ? this.selectedParent.id : undefined,
          ficheIncidentId: this.createModel.ficheIncidentId || undefined,
          projetId: this.createModel.projetId || undefined,
          participantIds: [],
        }
        const newId = await this.$http.myressif.chantiers.create(model)
        this.$bvModal.hide('chantier-create-modal')
        this.$emit('created', newId)
      } catch (e) {
        this.createError = 'La création du chantier a échoué.'
      } finally {
        this.creating = false
      }
    },
  },
}
</script>
